<script>
import BreadCrumbs from '@components/breadcrumbs/breadcrumbs-wrapper.vue';
import { clientMethods } from '@/src/state/helpers';
import { minLength, sameAs } from 'vuelidate/lib/validators';

export default {
  data() {
    return {
      loading: false,
      error: null,
      changeSuccessfully: false,
      password: '',
      confirmPassword: '',
    };
  },
  validations: {
    password: {
      minLength: minLength(3),
    },
    confirmPassword: {
      sameAsPassword: sameAs('password'),
    },
  },
  props: {
    hideRouterView: {
      type: Boolean,
      required: true,
    },
    isMobile: {
      type: Boolean,
      required: true,
    },
  },
  components: {
    BreadCrumbs,
  },
  mounted() {},
  computed: {
    cssVars() {
      return {
        '--color':process.env.VUE_APP_ORG_ID === '618183d65e84466f63c38878' ? '#FF7F50' : '#4059ff'  ,
      };
    },
  },
  methods: {
    ...clientMethods,
    async onChangePassword() {
      this.loading = true;
      this.$v.$touch();
      if (!this.$v.$invalid) {
        const response = await this.changePassword({
          password: this.password,
        });
        if (response.data.success) {
          this.password = '';
          this.confirmPassword = '';
          this.changeSuccessfully = true;
        }
      }
      this.loading = false;
    },
  },
};
</script>

<template>
  <div class="pw-container h-100">
    <BreadCrumbs class="second" v-if="!hideRouterView && isMobile" @back="$emit('hide-router-view')" prevPageName="password" />
    <b-row class="h-50">
      <b-col class="card">
        <div class="row h-100">
          <div class="wrapper">
            <span class="title">Change Password</span>
            <b-form-group id="password" label-for="input-1" description="" class="form-input">
              <b-form-input
                id="input-password"
                autocomplete
                type="password"
                placeholder="New Password"
                v-model.lazy="$v.password.$model"
              ></b-form-input>
            </b-form-group>
            <p class="errorMsg" v-if="$v.password.$error">Password must be at least {{ $v.password.$params.minLength.min }}</p>
            <b-form-group id="confirm-password" label-for="input-1" description="" class="form-input">
              <b-form-input
                autocomplete
                id="input-confirm-password"
                type="password"
                placeholder=" Confirm New Password"
                v-model.lazy="$v.confirmPassword.$model"
              ></b-form-input>
            </b-form-group>
            <p class="errorMsg" v-if="$v.confirmPassword.$error">Passwords do not match</p>
            <BaseButton type="submit" class="button btn-primary" :disabled="loading" @click.stop="onChangePassword">
              <b-spinner v-show="loading" small type="grow"></b-spinner>
              <span v-show="!loading">Update Password</span>
            </BaseButton>
          </div>
        </div>
      </b-col>
    </b-row>
    <b-row v-if="changeSuccessfully">
      <b-col class="confirmation-container">
        <div class="row">
          <div class="check-container">
            <svg width="66" height="66" viewBox="0 0 66 66" fill="none" xmlns="http://www.w3.org/2000/svg" v-bind:class="'icon-check'" v-bind:svg-inline="''" :style="cssVars" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path d="M33 66c18.225 0 33-14.775 33-33S51.225 0 33 0 0 14.775 0 33s14.775 33 33 33z" fill="url(#paint0_linear)" fill-opacity=".6"/><path d="M17.368 33.55l4.466-5.321 8.645 7.255 15.748-18.116 5.243 4.56L35.803 39.95l-4.466 5.322L17.368 33.55z" fill="#fff"/><defs><linearGradient id="paint0_linear" x1="33" y1="0" x2="33" y2="66" gradientUnits="userSpaceOnUse"><stop stop-color="#55BCF5"/><stop offset="1" stop-color="#3E4FE2"/></linearGradient></defs></svg>
          </div>
          <span class="update-message">Your password has successfully been updated!</span>
        </div>
      </b-col>
    </b-row>
  </div>
</template>

<style lang="scss" scoped>
@import '@design';
// stylelint-disable selector-pseudo-element-no-unknown
// stylelint-disable selector-id-pattern
.pw-container {
  background: #fff;
  width: 100%;
  .breadcrumb-wrapper {
    margin-bottom: 0;
    padding: 2rem;
  }
  .row {
    margin-left: unset;
    width: 100%;
  }
  .card {
    align-items: center;
    border: unset;
    margin-top: 2rem;
    padding: 3rem 4.5%;
    width: 80%;
    .wrapper {
      align-items: center;
      border-radius: 8px;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
      display: flex;
      flex-direction: column;
      font-style: normal;
      justify-content: center;
      width: 100%;
      .title {
        color: $color-page-header;
        font-family: $base-font-family;
        font-size: 25px;
        font-weight: 800;
        letter-spacing: -0.761905px;
        line-height: 52px;
        position: relative;
        text-align: center;
        width: 100%;
      }
      .form-input {
        width: 50%;
        input {
          background-color: #f3f3f3;
          border: unset;
          border-radius: 8px;
          margin-bottom: 0;
          padding: 22px;
          transition: 0.3s all ease-in-out;
          &:focus {
            background-color: #f3f3f3;
            box-shadow: 0 1px 1px rgba(0, 0, 0, 0.075) inset, 0 0 5px var(--BGColor);
          }
        }
        .is-valid {
          background-image: none;
        }
        .is-invalid {
          margin-bottom: 0;
        }
        textarea:hover,
        input:hover,
        textarea:active,
        input:active,
        textarea:focus,
        input:focus,
        button:focus,
        button:active,
        button:hover,
        label:focus,
        .btn:active,
        .btn.active {
          -webkit-appearance: none;
          outline: 0 !important;
        }
        ::-webkit-input-placeholder {
          /* Chrome/Opera/Safari */
          color: #000;
          opacity: 0.3;
        }
        ::-moz-placeholder {
          /* Firefox 19+ */
          color: #000;
          opacity: 0.3;
        }
        :-ms-input-placeholder {
          /* IE 10+ */
          color: #000;
          opacity: 0.3;
        }
        :-moz-placeholder {
          /* Firefox 18- */
          color: #000;
          opacity: 0.3;
        }
      }
      .errorMsg {
        border-color: #fd5252;
        border-width: 0.2rem;
        color: red;
        margin-bottom: 4.8%;
        margin-bottom: 1rem;
        margin-left: auto;
        margin-right: auto;
        text-align: center;
        width: 80%;
      }
      .btn-primary {
        align-items: center;
        background-color: $color-profile-primary-rgba;
        border: 2px solid $color-profile-primary-rgba;
        border-radius: 50px;
        font-family: $base-font-family;
        font-size: 14px;
        font-weight: 800;
        line-height: 22px;
        margin: 8px 0;
        padding: 1.2rem 5rem;
        top: calc(50% - 22px / 2);
        white-space: nowrap;
      }
    }
  }
  .confirmation-container {
    align-items: center;
    border: unset;
    display: flex;
    flex-direction: column;
    height: fit-content;
    justify-content: center;
    margin-top: 1rem;
    padding: 2.5rem 4.5%;
    position: relative;
    width: 80%;
    .row {
      align-items: center;
      border-radius: 8px;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
      color: #211f1f;
      display: flex;
      font-family: $proxima-nova;
      font-size: 18px;
      font-style: normal;
      font-weight: normal;
      justify-content: center;
      line-height: 22px;
      mix-blend-mode: normal;
      padding: 3rem 4.5%;

      .update-message {
        opacity: 0.8;
      }
      .check-container {
        align-items: center;
        display: flex;
        justify-content: center;
        margin-right: 1rem;
        .icon-check {
          height: 2rem;
          width: 2rem;
          path {
            &:first-child {
              fill: var(--color);
              fill-opacity: 1;
            }
          }
        }
      }
    }
  }
}
</style>
